import axios from 'axios';

// Base API URL - change this for production
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  delete: (id) => api.delete(`/projects/${id}`),
  saveFormData: (id, formData) => api.put(`/projects/${id}/form-data`, { formData }),
  getPlan: (id) => api.get(`/projects/${id}/plan`),
  downloadPlan: (id) => api.get(`/projects/${id}/download-plan`, { responseType: 'blob' }),
};

// Questions API
export const questionsAPI = {
  getAll: () => api.get('/projects/questions'),
};

// Payment API - STRIPE INTEGRATION
export const paymentAPI = {
  // Create Stripe checkout session
  createCheckoutSession: (projectId, purchaseType) =>
    api.post('/payment/create-checkout', { projectId, purchaseType }),

  // Verify payment status after Stripe redirect
  verifyPayment: (sessionId) => api.get(`/payment/verify/${sessionId}`),

  // Get payment history for current user
  getPaymentHistory: () => api.get('/payment/history'),

  // Download template bundle (for sample project)
  downloadTemplates: (projectId) =>
    api.get(`/payment/download-templates/${projectId}`, { responseType: 'blob' }),
};

// Config API
export const configAPI = {
  getConfig: () => api.get('/config'),
};

// Contact API
export const contactAPI = {
  submitContactForm: (formData) => api.post('/contact', formData),
};

// Sample Document API
export const sampleDocumentAPI = {
  downloadSamplePlan: () => {
    const baseUrl = API_URL.replace('/api', ''); // Remove /api suffix
    return `${baseUrl}/api/sample-document`;
  },
};

// Collaboration API
export const collaborationAPI = {
  // Invite management
  sendInvite: (projectId, email) => api.post(`/collaboration/projects/${projectId}/invite`, { email }),
  getPendingInvites: () => api.get('/collaboration/invites/pending'),
  getInviteByToken: (token) => api.get(`/collaboration/invites/${token}`),
  acceptInvite: (token) => api.post(`/collaboration/invites/${token}/accept`),
  declineInvite: (token) => api.post(`/collaboration/invites/${token}/decline`),
  getProjectInvites: (projectId) => api.get(`/collaboration/projects/${projectId}/invites`),
  revokeInvite: (projectId, inviteId) => api.delete(`/collaboration/projects/${projectId}/invites/${inviteId}`),

  // Member management
  getProjectMembers: (projectId) => api.get(`/collaboration/projects/${projectId}/members`),
  removeMember: (projectId, userId) => api.delete(`/collaboration/projects/${projectId}/members/${userId}`),
  leaveProject: (projectId) => api.post(`/collaboration/projects/${projectId}/leave`),
  transferOwnership: (projectId, newOwnerId) => api.post(`/collaboration/projects/${projectId}/transfer-ownership`, { newOwnerId }),
};

export default api;
